// L'EFFACEUR — SavingsCounter
// CCC.9 : compteur d'économies cumulées sur l'accueil dashboard.
// Affiche la somme des économies validées (Pack Audit) + estimées (autres missions).
// Si 0 → CTA Pack Audit gratuit ("combien tu paies en trop ?").
//
// Ne fait PAS d'appel API : on agrège côté client à partir des missions chargées
// dans OverviewTab, pour rester rapide et éviter un round-trip.

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Sparkles, ArrowRight, Euro } from 'lucide-react';

const computeTotalSavings = (missions) => {
  let validated = 0;  // Économies CONFIRMÉES (commission Audit Billing déjà calculée)
  let estimated = 0;  // Économies POTENTIELLES (estimation pas encore validée)

  for (const m of missions) {
    const billing = m.audit_billing || {};
    if (typeof billing.validated_savings_eur === 'number') {
      validated += billing.validated_savings_eur;
    }
    if (typeof m.total_annual_savings_potential === 'number') {
      estimated += m.total_annual_savings_potential;
    }
    if (typeof m.estimated_annual_savings === 'number') {
      estimated += m.estimated_annual_savings;
    }
  }

  return { validated: Math.round(validated), estimated: Math.round(estimated) };
};

// Compteur animé : commence à 0 et compte jusqu'à `target` en `duration` ms
const useCountUp = (target, duration = 1200) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === 0) {
      setValue(0);
      return;
    }
    const startTs = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - startTs;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
};

const SavingsCounter = ({ missions = [], setActiveTab }) => {
  const { validated, estimated } = computeTotalSavings(missions);
  const total = validated + estimated;
  const animatedTotal = useCountUp(total);

  // Wrapper cyber commun
  const CyberWrap = ({ children, success = true }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.14 }}
      data-testid="savings-counter"
      className={`relative overflow-hidden rounded-xl border-2 ${success ? 'border-success/40' : 'border-cyan-400/40'} ${success ? 'bg-gradient-to-r from-success/10 via-success/5 to-transparent' : 'bg-cyan-400/5'} p-5`}
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            `linear-gradient(${success ? 'rgba(0,255,148,0.5)' : 'rgba(0,245,255,0.5)'} 1px, transparent 1px), linear-gradient(90deg, ${success ? 'rgba(0,255,148,0.5)' : 'rgba(0,245,255,0.5)'} 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />
      <div
        className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${success ? 'via-success' : 'via-cyan-400'} to-transparent opacity-60 pointer-events-none`}
        style={{ animation: 'savings-scan 4.5s linear infinite' }}
      />
      <style>{`
        @keyframes savings-scan {
          0% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(90px); opacity: 0.9; }
          100% { transform: translateY(0); opacity: 0.4; }
        }
      `}</style>
      <div className="relative">{children}</div>
    </motion.div>
  );

  // Cas zéro : CTA Pack Audit
  if (total === 0) {
    return (
      <CyberWrap>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-success/15 border border-success/30 flex items-center justify-center flex-shrink-0 relative">
            <div className="absolute inset-0 bg-success/20 blur-xl rounded-lg" />
            <TrendingUp className="w-6 h-6 text-success relative" />
          </div>
          <div className="flex-1">
            <p className="text-success text-xs font-mono uppercase mb-0.5 tracking-[0.2em]">// AUDIT_GRATUIT</p>
            <p className="text-white text-base font-bold">Combien vous payez en trop chaque mois ?</p>
            <p className="text-gray-400 text-sm">
              Audit gratuit du relevé bancaire, 29€ uniquement si vous voulez lancer les résiliations.
            </p>
          </div>
          <button
            onClick={() => setActiveTab && setActiveTab('payment')}
            className="bg-success hover:bg-success/90 text-black font-bold px-4 py-2 rounded-lg flex items-center gap-1 text-sm flex-shrink-0 shadow-[0_0_20px_rgba(0,255,148,0.3)]"
            data-testid="audit-cta"
          >
            Tester <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </CyberWrap>
    );
  }

  return (
    <CyberWrap>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-success/15 border border-success/40 flex items-center justify-center flex-shrink-0 relative">
          <div className="absolute inset-0 bg-success/30 blur-xl rounded-lg" />
          <Euro className="w-7 h-7 text-success relative" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-success text-xs font-mono uppercase mb-0.5 tracking-[0.2em]">
            // ÉCONOMIES_{validated > 0 ? 'CUMULÉES' : 'ESTIMÉES'}
          </p>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-orbitron text-3xl md:text-4xl font-black text-success" style={{ textShadow: '0 0 20px rgba(0,255,148,0.5)' }}>
              {animatedTotal.toLocaleString('fr-FR')}€
            </span>
            <span className="text-xs text-gray-500 font-mono">/an</span>
          </div>
          <p className="text-gray-400 text-xs mt-0.5">
            {validated > 0 && (<><span className="text-success font-bold">{validated.toLocaleString('fr-FR')}€</span> validés</>)}
            {validated > 0 && estimated > 0 && ' · '}
            {estimated > 0 && (<><span className="text-cyan-400 font-bold">{estimated.toLocaleString('fr-FR')}€</span> estimés</>)}
            {' · '}grâce à L'EFFACEUR
          </p>
        </div>
        <Sparkles className="w-5 h-5 text-success animate-pulse flex-shrink-0" />
      </div>
    </CyberWrap>
  );
};

export default SavingsCounter;
